import {
  SmsChannel,
  prisma,
  UnifonicChannel,
  ResourceType,
  ActionType,
  CommunicationLevel,
  SmsChannelType,
} from "client";
// CREATE
export const createSmsChannel = async (userId: string, data: SmsChannel) => {
  try {
        const smsChannel = await prisma.smsChannel.create({
      data: {
        communicationChannel: { connect: { id: data.communicationChannelId } },
        type: data.type,
      },
    });
    return { smsChannel };
  } catch (error) {
    return { error };
  }
};
// crete sms channel with twilio auth token and account sid and phone number and communication channel id
export const createSmsChannelWithTwilio = async (
  entityId: string,
  twilioAuthToken: string,
  twilioAccountSid: string,
  twilioPhoneNumber: string,
  communicationChannelId?: string,
  level?: CommunicationLevel
) => {
  try {
    let connect = {};
    switch (level) {
      case CommunicationLevel.WHITE_LABEL:
        connect = { whiteLabel: { connect: { id: entityId } } };
        break;
      case CommunicationLevel.EVENT:
        connect = { event: { connect: { id: entityId } } };
        break;
      case CommunicationLevel.ORGANIZATION:
        connect = { organization: { connect: { id: entityId } } };
        break;
      case CommunicationLevel.EXHIBITOR:
        connect = { exhibitor: { connect: { id: entityId } } };
        break;

      default:
        connect = { whiteLabel: { connect: { id: entityId } } };
        break;
    }
    const communicationChannel = communicationChannelId
      ? null
      : await prisma.communicationChannel.create({
          data: {
            level: level || CommunicationLevel.WHITE_LABEL,
            ...connect,
          },
        });
    const smsChannel = await prisma.smsChannel.create({
      data: {
        communicationChannel: {
          connect: { id: communicationChannel?.id || communicationChannelId },
        },
        type: SmsChannelType.TWILIO,
        twilioChannel: {
          create: {
            authToken: twilioAuthToken,
            accountSid: twilioAccountSid,
            phoneNumber: twilioPhoneNumber,
          },
        },
      },
    });

    return { smsChannel };
  } catch (error) {
    return { error };
  }
};

export const createSmsChannelWithUnifonic = async (
  entityId: string,
  channelData: UnifonicChannel,
  communicationChannelId?: string,
  level?: CommunicationLevel
) => {
  try {
    let connect = {};
    switch (level) {
      case CommunicationLevel.WHITE_LABEL:
        connect = { whiteLabel: { connect: { id: entityId } } };
        break;
      case CommunicationLevel.EVENT:
        connect = { event: { connect: { id: entityId } } };
        break;
      case CommunicationLevel.ORGANIZATION:
        connect = { organization: { connect: { id: entityId } } };
        break;
      case CommunicationLevel.EXHIBITOR:
        connect = { exhibitor: { connect: { id: entityId } } };
        break;

      default:
        connect = { whiteLabel: { connect: { id: entityId } } };
        break;
    }
    const communicationChannel = communicationChannelId
      ? null
      : await prisma.communicationChannel.create({
          data: {
            level: level || CommunicationLevel.WHITE_LABEL,
            ...connect,
          },
        });
    const smsChannel = await prisma.smsChannel.create({
      data: {
        communicationChannel: {
          connect: { id: communicationChannel?.id || communicationChannelId },
        },
        type: SmsChannelType.UNIFONIC,
        unifonicChannel: {
          create: {
            ...channelData,
          },
        },
      },
    });

    return { smsChannel };
  } catch (error) {
    return { error };
  }
};

// READ
export const getAllSmsChannels = async (
  userId: string
): Promise<{
  smsChannels?: SmsChannel[];
  error?: any;
}> => {
  try {
        const smsChannels = await prisma.smsChannel.findMany();
    return { smsChannels };
  } catch (error) {
    return { error };
  }
};

export const getSmsChannelById = async (id: string) => {
  try {
    const smsChannel = await prisma.smsChannel.findUnique({
      where: { id },
    });
    return { smsChannel };
  } catch (error) {
    return { error };
  }
};

export const getSmsChannelByCommuncationChannelsId = async (
  communicationChannelId: string
) => {
  try {
    const smsChannel = await prisma.smsChannel.findUnique({
      where: { communicationChannelId },
    });
    return { smsChannel };
  } catch (error) {
    return { error };
  }
};

export const getSmsChannelDataByCommuncationChannelsIdWithTwilio = async (
  communicationChannelId: string
) => {
  try {
    const smsChannel = await prisma.smsChannel.findUnique({
      where: { communicationChannelId },
      include: { twilioChannel: true, unifonicChannel: true },
    });
    return smsChannel;
  } catch (error) {
    return { error };
  }
};

// UPDATE
export const updateSmsChannelById = async (
  userId: string,
  id: string,
  data: SmsChannel
) => {
  try {
        const smsChannel = await prisma.smsChannel.update({
      where: { id },
      data: {
        communicationChannel: { connect: { id: data.communicationChannelId } },
        type: data.type,
      },
    });
    return { smsChannel };
  } catch (error) {
    return { error };
  }
};

export const updateSmsChannelWithTwilio = async (
  userId: string,
  id: string,
  authToken: string | undefined,
  accountSid: string,
  phoneNumber: string
) => {
  try {
    const smsChannel = await prisma.smsChannel.update({
      where: { id },
      data: {
        twilioChannel: {
          update: {
            ...(authToken !== undefined && { authToken }),
            accountSid,
            phoneNumber,
          },
        },
      },
    });
    return { smsChannel };
  } catch (error) {
    return { error };
  }
};

export const updateSmsChannelWithUnifonic = async (
  userId: string,
  id: string,
  channelData: Partial<UnifonicChannel>
) => {
  try {
    const updateData = { ...channelData };
    if (updateData.password === undefined) delete updateData.password;
    if (updateData.secret === undefined) delete updateData.secret;
    const smsChannel = await prisma.smsChannel.update({
      where: { id },
      data: {
        type: SmsChannelType.UNIFONIC,
        unifonicChannel: {
          update: updateData,
        },
      },
    });
    return { smsChannel };
  } catch (error) {
    return { error };
  }
};

// DELETE
export const deleteSmsChannelById = async (userId: string, id: string) => {
  try {
        const smsChannel = await prisma.smsChannel.delete({
      where: { id },
    });
    return { smsChannel };
  } catch (error) {
    return { error };
  }
};
